import React, { useState, useEffect } from 'react';
import { Star, Plus, Edit, Trash2, Save, X, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [dynamicReviewsEnabled, setDynamicReviewsEnabled] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [syncFrequency, setSyncFrequency] = useState('daily');
  const [minRating, setMinRating] = useState(4);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [googleReviewsCount, setGoogleReviewsCount] = useState(0);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [apiMethod, setApiMethod] = useState('simple'); // Track API method from CMS settings
  const [formData, setFormData] = useState({
    text: '',
    pet_name: '',
    owner_name: ''
  });
  const [authError, setAuthError] = useState(null); // New state for authentication errors
  const [pageLoading, setPageLoading] = useState(true); // New state for page loading

  const { user, token, isAdmin, canAccessManager, loading: authLoading } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Authentication useEffect - same pattern as ConfigureHours and BusinessInfo
  useEffect(() => {
    // Wait for auth to finish loading before checking permissions
    if (authLoading) {
      return;
    }

    // If there's a token but no user yet, wait for user profile to load
    if (token && !user) {
      return;
    }

    if (user && canAccessManager()) {
      setAuthError(null);
      setPageLoading(false);
      fetchReviews();
    } else if (user && !canAccessManager()) {
      setAuthError('Access denied. Manager or admin privileges required.');
      setPageLoading(false);
    } else if (!token) {
      // Only show error if there's definitely no token
      setAuthError('You must be logged in as a manager or admin to manage reviews.');
      setPageLoading(false);
    }
  }, [user, token, authLoading]);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/reviews/manage`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews);
        // Set dynamic reviews setting if available
        if (data.dynamic_reviews_enabled !== undefined) {
          setDynamicReviewsEnabled(data.dynamic_reviews_enabled);
        }
        // Set sync settings
        if (data.sync_frequency) {
          setSyncFrequency(data.sync_frequency);
        }
        if (data.min_rating !== undefined) {
          setMinRating(data.min_rating);
        }
        if (data.last_sync_time) {
          setLastSyncTime(data.last_sync_time);
        }
        if (data.google_reviews_count !== undefined) {
          setGoogleReviewsCount(data.google_reviews_count);
        }
      } else {
        throw new Error('Failed to fetch reviews');
      }
      
      // Fetch API method from CMS settings
      const cmsResponse = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (cmsResponse.ok) {
        const cmsData = await cmsResponse.json();
        setApiMethod(cmsData.google_reviews_api_method || 'simple');
      }
    } catch (err) {
      setError('Failed to load reviews');
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDynamicReviews = async (enabled) => {
    setSavingSettings(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/reviews/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          dynamic_reviews_enabled: enabled,
          sync_frequency: syncFrequency,
          min_rating: minRating
        })
      });

      if (response.ok) {
        setDynamicReviewsEnabled(enabled);
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (err) {
      console.error('Error updating review settings:', err);
      setError('Failed to update review settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveSyncSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/reviews/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          dynamic_reviews_enabled: dynamicReviewsEnabled,
          sync_frequency: syncFrequency,
          min_rating: minRating
        })
      });

      if (response.ok) {
        setNotification({ show: true, message: 'Sync settings saved successfully!', type: 'success' });
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving sync settings:', err);
      setNotification({ show: true, message: 'Failed to save sync settings', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/reviews/sync-google`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLastSyncTime(data.last_sync_time);
        setGoogleReviewsCount(data.total_reviews);
        
        // Check if OAuth error
        if (!data.success && data.error && data.error.includes('OAuth')) {
          setNotification({ 
            show: true, 
            message: data.error, 
            type: 'error' 
          });
        } else {
          setNotification({ 
            show: true, 
            message: `Successfully synced! ${data.new_reviews || 0} new reviews added.`, 
            type: 'success' 
          });
        }
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
        fetchReviews(); // Refresh the page data
      } else {
        throw new Error('Failed to sync reviews');
      }
    } catch (err) {
      console.error('Error syncing reviews:', err);
      setNotification({ 
        show: true, 
        message: 'Failed to sync Google reviews. Please check your API credentials.', 
        type: 'error' 
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
    } finally {
      setRefreshing(false);
    }
  };

  const handleOAuthAuthorize = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/reviews/oauth/authorize`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.authorization_url) {
          // Open authorization URL in new window
          window.open(data.authorization_url, '_blank', 'width=600,height=700');
          setNotification({ 
            show: true, 
            message: 'Opening Google authorization. Please grant access.', 
            type: 'success' 
          });
          setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
        } else {
          throw new Error(data.detail || 'Failed to get authorization URL');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to initiate OAuth');
      }
    } catch (err) {
      console.error('Error initiating OAuth:', err);
      setNotification({ 
        show: true, 
        message: err.message || 'Failed to start OAuth authorization', 
        type: 'error' 
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
    }
  };

  useEffect(() => {
    // Only fetch if user is logged in and has manager/admin privileges
    if (user && canAccessManager()) {
      fetchReviews();
    } else if (user && !canAccessManager()) {
      setError('Access denied. Manager or admin privileges required.');
      setLoading(false);
    } else {
      setError('Please log in as a manager or admin to manage reviews.');
      setLoading(false);
    }
  }, [user, canAccessManager]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchReviews();
        setShowAddForm(false);
        setFormData({ text: '', pet_name: '', owner_name: '' });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add review');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditReview = async (reviewId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/reviews/${reviewId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchReviews();
        setEditingId(null);
        setFormData({ text: '', pet_name: '', owner_name: '' });
      } else {
        throw new Error('Failed to update review');
      }
    } catch (err) {
      setError('Failed to update review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchReviews();
      } else {
        throw new Error('Failed to delete review');
      }
    } catch (err) {
      setError('Failed to delete review');
    }
  };

  const startEdit = (review) => {
    setEditingId(review.id);
    setFormData({
      text: review.text,
      pet_name: review.pet_name,
      owner_name: review.owner_name
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData({ text: '', pet_name: '', owner_name: '' });
  };

  // Show compact loading modal while authentication is being determined
  if (authLoading || pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto mb-3"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading</h3>
            <p className="text-sm text-gray-600">
              {authLoading ? 'Verifying your access permissions...' : 'Loading review data...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (authError) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">{authError}</p>
            <p className="text-red-600 text-sm mt-2">Please log in with an admin account.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className={`rounded-lg shadow-lg p-4 max-w-md ${
            notification.type === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <div className="rounded-full bg-green-100 p-1">
                    <Star className="h-5 w-5 text-green-600" />
                  </div>
                ) : (
                  <div className="rounded-full bg-red-100 p-1">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification({ show: false, message: '', type: '' })}
                className={`flex-shrink-0 ${
                  notification.type === 'success' ? 'text-green-400 hover:text-green-600' : 'text-red-400 hover:text-red-600'
                }`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Inside the main card */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-medium text-gray-900">
                  Manage Reviews
                </h2>
                {reviews.length < 3 && !showAddForm && !dynamicReviewsEnabled && (
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Review
                  </button>
                )}
              </div>

              {/* Dynamic Google Reviews Toggle */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <Star className="h-5 w-5 text-theme-blue" />
                  </div>
                  <div>
                    <h3 className="font-medium text-sm text-gray-900">Dynamic Google Reviews</h3>
                    <p className="text-xs text-gray-600">
                      Display live Google reviews on your website instead of manual reviews
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dynamicReviewsEnabled}
                    onChange={(e) => handleToggleDynamicReviews(e.target.checked)}
                    disabled={savingSettings}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-theme-blue disabled:opacity-50 disabled:cursor-not-allowed"></div>
                </label>
              </div>

              {/* Info message when dynamic reviews is enabled */}
              {dynamicReviewsEnabled && (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-yellow-800">
                          <strong>Dynamic Reviews Enabled:</strong> Your website is displaying live Google reviews ({googleReviewsCount} total). 
                          Configure settings below to control sync frequency and rating filters.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Info message about API configuration */}
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="h-4 w-4 text-theme-blue mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-blue-800">
                          <strong>API Configuration:</strong> Configure your Google API credentials and method in <a href="/cms-settings" className="underline hover:text-blue-900">CMS Settings → Google Settings</a>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* OAuth Authorization Button (only show if OAuth method is selected) */}
                  {apiMethod === 'oauth' && (
                    <div className="bg-white border border-gray-200 rounded-md p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">OAuth Authorization</h3>
                      <p className="text-xs text-gray-600 mb-3">
                        You've selected OAuth API in CMS Settings. Click below to authorize access to your Google Business Profile
                      </p>
                      <button
                        onClick={handleOAuthAuthorize}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-theme-blue hover:bg-blue-700 rounded-md transition-colors"
                      >
                        🔐 Authorize with Google
                      </button>
                    </div>
                  )}

                  {/* Sync Configuration */}
                  <div className="bg-white border border-gray-200 rounded-md p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Sync Configuration</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* Sync Frequency */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Sync Frequency
                        </label>
                        <select
                          value={syncFrequency}
                          onChange={(e) => setSyncFrequency(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="daily">Daily (automatic)</option>
                          <option value="weekly">Weekly (automatic)</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Reviews will automatically sync at this frequency
                        </p>
                      </div>

                      {/* Minimum Rating Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Minimum Rating to Display
                        </label>
                        <select
                          value={minRating}
                          onChange={(e) => setMinRating(parseInt(e.target.value))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={5}>5 Stars Only ⭐⭐⭐⭐⭐</option>
                          <option value={4}>4+ Stars ⭐⭐⭐⭐</option>
                          <option value={3}>3+ Stars ⭐⭐⭐</option>
                          <option value={1}>All Ratings</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Only show reviews with this rating or higher
                        </p>
                      </div>
                    </div>

                    {/* Last Sync Info & Actions */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t border-gray-200">
                      <div>
                        {lastSyncTime && (
                          <p className="text-xs text-gray-600">
                            Last synced: <strong>{new Date(lastSyncTime).toLocaleString()}</strong>
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {googleReviewsCount} Google reviews stored in database
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleManualRefresh}
                          disabled={refreshing}
                          className="inline-flex items-center px-3 py-1.5 text-sm text-white bg-theme-blue hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
                        >
                          {refreshing ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Syncing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Sync Now
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleSaveSyncSettings}
                          disabled={savingSettings}
                          className="inline-flex items-center px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50"
                        >
                          {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Show manual reviews management only when dynamic reviews is disabled */}
        {!dynamicReviewsEnabled && (
          <>
            {/* Add Review Form */}
            {showAddForm && (
              <div className="border-t border-gray-200 pt-6 mt-6">
            <h2 className="text-base font-medium text-gray-900 mb-4">Add New Review</h2>
            <form onSubmit={handleAddReview}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pet Owner Name
                  </label>
                  <input
                    type="text"
                    name="owner_name"
                    value={formData.owner_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-theme-blue"
                    style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--theme-primary)'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    placeholder="e.g. Sarah Johnson"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pet Name
                  </label>
                  <input
                    type="text"
                    name="pet_name"
                    value={formData.pet_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-theme-blue"
                    style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--theme-primary)'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    placeholder="e.g. Luna"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review Text
                </label>
                <textarea
                  name="text"
                  value={formData.text}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-theme-blue"
                  style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--theme-primary)'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  placeholder="Write the customer review here..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Review
                </button>
              </div>
            </form>
              </div>
            )}

            {/* Reviews List */}
            <div className={`space-y-6 ${showAddForm ? 'border-t border-gray-200 pt-6 mt-6' : ''}`}>
              {reviews.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500">No reviews yet. Add your first review!</p>
                </div>
              ) : (
                reviews.map((review) => (
                  <div key={review.id} className="border border-gray-200 rounded-lg p-6">
                {editingId === review.id ? (
                  /* Edit Form */
                  <form onSubmit={(e) => { e.preventDefault(); handleEditReview(review.id); }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pet Owner Name
                        </label>
                        <input
                          type="text"
                          name="owner_name"
                          value={formData.owner_name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-theme-blue"
                          style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                          onFocus={(e) => e.target.style.borderColor = 'var(--theme-primary)'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pet Name
                        </label>
                        <input
                          type="text"
                          name="pet_name"
                          value={formData.pet_name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-theme-blue"
                          style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                          onFocus={(e) => e.target.style.borderColor = 'var(--theme-primary)'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Review Text
                      </label>
                      <textarea
                        name="text"
                        value={formData.text}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-theme-blue"
                        style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                        onFocus={(e) => e.target.style.borderColor = 'var(--theme-primary)'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <X className="h-4 w-4 mr-2 inline" />
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                        style={{ backgroundColor: 'var(--theme-primary)' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </button>
                    </div>
                  </form>
                ) : (
                  /* Display Review */
                  <div>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEdit(review)}
                          className="p-2 text-gray-500 hover:text-theme-blue transition-colors"
                          title="Edit review"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteReview(review.id)}
                          className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                          title="Delete review"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mb-4 italic">"{review.text}"</p>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{review.owner_name}</p>
                      <p className="text-xs text-gray-600">Pet parent to {review.pet_name}</p>
                    </div>
                  </div>
                )}
                  </div>
                ))
              )}
            </div>

            {/* Information Note */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Star className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    About Reviews
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Maximum of 3 reviews can be displayed on the home page</li>
                      <li>All reviews automatically have 5-star ratings</li>
                      <li>Reviews are displayed as "Pet Owner Name - Pet parent to Pet Name"</li>
                      <li>Changes appear immediately on the home page</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Reviews;